import G6 from '@antv/g6';
import { isProd } from 'utils/env';
import noop from 'utils/noop';
import { ItemType, ItemState, GraphState, EditorEvent } from '../constants';
import { Graph, EdgeModel, Item, Node, Edge } from '../types';

/** Generate unique identifier */
export function guid() {
  return 'xxxxxxxx'.replace(/[xy]/g, function getRandomString(c) {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Implement batch processing */
export function executeBatch(graph: Graph | null | undefined, execute: (...args: any[]) => void) {
  const autoPaint = graph?.get('autoPaint');

  graph?.setAutoPaint(false);

  execute();

  graph?.paint();
  graph?.setAutoPaint(autoPaint);
}

/** Is this a flow chart? */
export function isFlow(graph?: Graph | null) {
  const isFlowGraph = graph ? graph.constructor === G6.Graph : false;

  /* istanbul ignore next */
  if (!isFlowGraph && !isProd()) {
    console.warn(`
    You are using an unsupported graph type.

    Currently, the only graph type that is supported by FlowChart is 'G6.Graph', which is G6's default graph type.
    See further documentation here: https://g6.antv.vision/en/docs/api/Graph
    `);
  }

  return isFlowGraph;
}

/** Is this a mind map? */
export function isMind(graph?: Graph | null) {
  const isTreeGraph = graph ? graph.constructor === G6.TreeGraph : false;

  /* istanbul ignore next */
  if (isTreeGraph && !isProd()) {
    console.warn(`
    TreeGraphs are not currently supported by FlowChart.

    Obvious changes are identified and documented in the code.
    Search 'TreeGraph' to find places which needs to be changed.

    See further documentation here: https://g6.antv.vision/en/docs/api/TreeGraph
    `);
  }

  return isTreeGraph;
}

/** Is this a node? */
export function isNode(item?: Item | null): item is Node {
  return item?.getType() === ItemType.Node;
}

/** Is this an edge? */
export function isEdge(item: Item): item is Edge {
  return item.getType() === ItemType.Edge;
}

/** Get the selected nodes */
export function getSelectedNodes(graph: Graph | null): Node[] {
  return graph?.findAllByState(ItemType.Node, ItemState.Selected) || [];
}

/** Get the selected edges */
export function getSelectedEdges(graph: Graph | null): Edge[] {
  return graph?.findAllByState(ItemType.Edge, ItemState.Selected) || [];
}

/** Get the highlight edges */
export function getHighlightEdges(graph: Graph | null): Edge[] {
  return graph?.findAllByState(ItemType.Edge, ItemState.HighLight) || [];
}

/** Get the chart status */
export function getGraphState(graph: Graph): GraphState {
  let graphState: GraphState = GraphState.MultiSelected;

  const selectedNodes = getSelectedNodes(graph);
  const selectedEdges = getSelectedEdges(graph);

  if (selectedNodes.length === 1 && !selectedEdges.length) {
    graphState = GraphState.NodeSelected;
  }

  if (selectedEdges.length === 1 && !selectedNodes.length) {
    graphState = GraphState.EdgeSelected;
  }

  if (!selectedNodes.length && !selectedEdges.length) {
    graphState = GraphState.CanvasSelected;
  }

  return graphState;
}

/** Set selected element */
export function setSelectedItems(graph: Graph, items: Item[] | string[]) {
  executeBatch(graph, () => {
    const selectedNodes = getSelectedNodes(graph);
    const selectedEdges = getSelectedEdges(graph);

    [...selectedNodes, ...selectedEdges].forEach((node) => {
      graph.setItemState(node, ItemState.Selected, false);
    });

    items.forEach((item) => {
      graph.setItemState(item, ItemState.Selected, true);
    });
  });

  graph.emit(EditorEvent.onGraphStateChange, {
    graphState: getGraphState(graph),
  });
}

/** Clear selected status */
export function clearSelectedState(
  graph: Graph | null | undefined,
  shouldUpdate: (item: Item) => boolean = () => true,
) {
  if (!graph) return;

  const selectedNodes = getSelectedNodes(graph);
  const selectedEdges = getSelectedEdges(graph);

  executeBatch(graph, () => {
    [...selectedNodes, ...selectedEdges].forEach((item) => {
      if (shouldUpdate(item)) {
        graph.setItemState(item, ItemState.Selected, false);
      }
    });
  });
}

/** Get a backtrack path - Flow */
export function getFlowRecallEdges(
  graph: Graph | null | undefined,
  node: Node,
  targetIds: string[] = [],
  edges: Edge[] = [],
) {
  const inEdges: Edge[] = node.getInEdges();

  if (!inEdges.length || !graph) return [];

  // eslint-disable-next-line array-callback-return
  inEdges.map((edge) => {
    const sourceId = (edge.getModel() as EdgeModel).source;

    if (!sourceId) return;

    const sourceNode = graph?.findById(sourceId) as Node;

    edges.push(edge);

    const targetId = node.get('id');

    targetIds.push(targetId);

    if (!targetIds.includes(sourceId)) {
      getFlowRecallEdges(graph, sourceNode, targetIds, edges);
    }
  });

  return edges;
}

interface TraverseNodeInfo {
  node: Node;
  edge: Edge | null;
  outEdges: Edge[];
  layerIndex: number;
  branchIndex: number;
}

interface TraverseNodeOptions {
  /** called for each node */
  nodeFn?: (info: TraverseNodeInfo) => void;
  /** called for each edge */
  edgeFn?: (info: TraverseNodeInfo) => void;
}

type TraverseNodeInternalArgs = Pick<TraverseNodeInfo, 'layerIndex' | 'branchIndex'> & {
  visitedNodes: string[];
};

/** Step through a node tree and call a function for each node and edge */
export const traverseNodes = (
  node: Node,
  opts: TraverseNodeOptions = {},
  internalArgs: TraverseNodeInternalArgs = { layerIndex: 0, branchIndex: 0, visitedNodes: [] },
) => {
  const { nodeFn = noop, edgeFn = noop } = opts;
  const { layerIndex = 0, branchIndex = 0, visitedNodes = [] } = internalArgs;
  const outEdges = node.getOutEdges();
  const isMultiNode = outEdges.length > 1;

  visitedNodes.push(node.getID());

  nodeFn({ node, edge: null, outEdges, layerIndex, branchIndex });

  // eslint-disable-next-line for-direction
  for (let i = outEdges.length; i > 0; i--) {
    const edge = outEdges[i - 1];
    const { sourceAnchor = 1, target } = edge.getModel() as EdgeModel;
    const targetBranchIndex = (isMultiNode ? branchIndex * 2 : branchIndex) + sourceAnchor - 1;
    const args = { node, edge, outEdges, layerIndex, branchIndex: targetBranchIndex };

    edgeFn(args);

    // If there is a cycle, stop iterating
    if (!target || visitedNodes.includes(target)) break;

    traverseNodes(edge.getTarget(), opts, {
      layerIndex: layerIndex + 1,
      branchIndex: args.branchIndex,
      visitedNodes,
    });
  }
};
